import React from 'react';
import LegalPage, { LegalSection, LegalContactInfo } from '../components/LegalPage';

const Privacy = () => {
  return (
    <LegalPage
      title="Privacy Policy"
      lastUpdated="January 2025"
      intro="Travel Mhor is committed to protecting your privacy. This policy explains how we collect, use, and protect your personal information when you use our services or visit our website."
      footerText="By using our services, you acknowledge that you have read and understood this Privacy Policy."
    >
      <LegalSection title="1. Information We Collect">
        <h3>Personal Information</h3>
        <p>When you book a tour or contact us, we may collect:</p>
        <ul>
          <li>Name and contact information (email, phone number, address)</li>
          <li>Payment information (processed securely through third-party providers)</li>
          <li>Emergency contact details</li>
          <li>Special requirements (dietary restrictions, mobility needs, medical conditions)</li>
          <li>Age and group composition for appropriate tour planning</li>
        </ul>

        <h3>Automatically Collected Information</h3>
        <p>When you visit our website, we may automatically collect:</p>
        <ul>
          <li>IP address and browser information</li>
          <li>Pages visited and time spent on our site</li>
          <li>Referring website and search terms</li>
          <li>Device type and operating system</li>
        </ul>
      </LegalSection>

      <LegalSection title="2. How We Use Your Information">
        <p>We use your personal information to:</p>
        <ul>
          <li>Process and confirm your tour bookings</li>
          <li>Communicate important information about your tours</li>
          <li>Provide customer support and respond to inquiries</li>
          <li>Improve our services and website functionality</li>
          <li>Send promotional emails (only with your consent)</li>
          <li>Comply with legal requirements and safety regulations</li>
          <li>Process payments and prevent fraud</li>
        </ul>
      </LegalSection>

      <LegalSection title="3. Information Sharing and Disclosure">
        <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only in these circumstances:</p>
        <ul>
          <li><strong>Service Providers:</strong> With trusted partners who help us operate our business (payment processors, email services)</li>
          <li><strong>Legal Requirements:</strong> When required by law, court order, or legal process</li>
          <li><strong>Safety and Emergency:</strong> To protect the safety and rights of our customers, staff, or the public</li>
          <li><strong>Business Transfer:</strong> In the event of a merger, acquisition, or sale of assets</li>
        </ul>
      </LegalSection>

      <LegalSection title="4. Data Security">
        <p>We implement appropriate security measures to protect your personal information:</p>
        <ul>
          <li>Secure SSL encryption for data transmission</li>
          <li>Regular security assessments and updates</li>
          <li>Limited access to personal data on a need-to-know basis</li>
          <li>Secure storage systems with backup and recovery procedures</li>
          <li>Staff training on data protection and privacy practices</li>
        </ul>
        <p>However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.</p>
      </LegalSection>

      <LegalSection title="5. Data Retention">
        <p>We retain your personal information only as long as necessary for the purposes outlined in this policy:</p>
        <ul>
          <li><strong>Booking Information:</strong> Retained for 7 years for accounting and legal purposes</li>
          <li><strong>Marketing Consent:</strong> Until you withdraw consent or we determine it's no longer valid</li>
          <li><strong>Website Analytics:</strong> Anonymized data may be retained indefinitely for business insights</li>
          <li><strong>Customer Support:</strong> Communication records retained for 2 years</li>
        </ul>
      </LegalSection>

      <LegalSection title="6. Your Rights and Choices">
        <p>You have the following rights regarding your personal information:</p>
        <ul>
          <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
          <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
          <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
          <li><strong>Portability:</strong> Request transfer of your data in a machine-readable format</li>
          <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
          <li><strong>Restriction:</strong> Request limitation of how we process your information</li>
        </ul>
        <p>To exercise these rights, contact us using the information provided below.</p>
      </LegalSection>

      <LegalSection title="7. Cookies and Tracking Technologies">
        <p>Our website uses cookies and similar technologies to:</p>
        <ul>
          <li>Remember your preferences and settings</li>
          <li>Analyze website traffic and user behavior</li>
          <li>Improve website functionality and user experience</li>
          <li>Provide relevant content and advertisements</li>
        </ul>
        <p>You can control cookie preferences through your browser settings. Disabling certain cookies may affect website functionality.</p>
      </LegalSection>

      <LegalSection title="8. Third-Party Links">
        <p>Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to read the privacy policies of any websites you visit.</p>
      </LegalSection>

      <LegalSection title="9. Children's Privacy">
        <p>Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will promptly delete it.</p>
      </LegalSection>

      <LegalSection title="10. International Data Transfers">
        <p>Your information may be processed and stored in countries other than your residence. We ensure appropriate safeguards are in place to protect your information during international transfers.</p>
      </LegalSection>

      <LegalSection title="11. Changes to This Privacy Policy">
        <p>We may update this privacy policy periodically to reflect changes in our practices or legal requirements. We will notify you of significant changes by posting the updated policy on our website and updating the "Last Updated" date.</p>
      </LegalSection>

      <LegalSection title="12. Contact Us">
        <p>If you have questions about this privacy policy or our data practices, please contact us:</p>
        <LegalContactInfo />
        <p>We will respond to your inquiries within 30 days.</p>
      </LegalSection>
    </LegalPage>
  );
};

export default Privacy;
